import type {
  KonvaAnnotationGeometry,
  KonvaAnnotationTemplate,
} from './annotationSchema'
import { getAbsoluteYFromSubPage } from './utils'

export type LineBasedAnnotation = KonvaAnnotationTemplate & {
  geometry: Extract<KonvaAnnotationGeometry, { mode: 'points' }>
  kind: 'line' | 'arrow' | 'bubble'
}

// Coordinates inside an annotation's own Konva group.
export type LocalAnnotationRect = {
  height: number
  width: number
  x: number
  y: number
}

// Coordinates in the full document page/subpage space.
export type DocumentAnnotationBounds = {
  height: number
  left: number
  top: number
  width: number
}

export type SelectionFrame =
  | {
      mode: 'axis'
      center: { x: number; y: number }
      cornerRadius: number
      rect: LocalAnnotationRect
    }
  | {
      mode: 'oriented'
      angle: number
      center: { x: number; y: number }
      cornerRadius: number
      origin: { x: number; y: number }
      rect: LocalAnnotationRect
    }

export type ArrowTextLayout = {
  arrowPoints: number[]
  fontSize: number
  placement: 'middle' | 'bottom'
  text: string
  textCornerRadius: number
  textHeight: number
  textWidth: number
  textX: number
  textY: number
}

export type BubbleLayout = {
  bubbleCenter: { x: number; y: number }
  bubbleRadius: number
  linePoints: number[]
}

export type LineResizeHandleGeometry = {
  handleOffsetX: number
  handleOffsetY: number
  index: number
  x: number
  y: number
}

export const AXIS_SELECTION_PADDING = 6
export const DEFAULT_BUBBLE_RADIUS = 10
export const LINE_RESIZE_HANDLE_OFFSET = 10
export const ORIENTED_POINT_SELECTION_END_PADDING = 10

export const isLineBasedAnnotation = (
  annotation: KonvaAnnotationTemplate,
): annotation is LineBasedAnnotation =>
  annotation.geometry.mode === 'points' &&
  (annotation.kind === 'line' ||
    annotation.kind === 'arrow' ||
    annotation.kind === 'bubble')

export const getLineVector = (points: number[]) => {
  const [x1 = 0, y1 = 0, x2 = 0, y2 = 0] = points
  const dx = x2 - x1
  const dy = y2 - y1
  const length = Math.hypot(dx, dy)
  const ux = length ? dx / length : 0
  const uy = length ? dy / length : 0

  return {
    angleDeg: Math.atan2(dy, dx) * (180 / Math.PI),
    dx,
    dy,
    length,
    ux,
    uy,
    x1,
    x2,
    y1,
    y2,
  }
}

export const getPointExtents = ({
  maxXFallback = 0,
  maxYFallback = 0,
  minXFallback = 0,
  minYFallback = 0,
  points,
}: {
  maxXFallback?: number
  maxYFallback?: number
  minXFallback?: number
  minYFallback?: number
  points: number[]
}) => {
  const xs = points.filter((_, index) => index % 2 === 0)
  const ys = points.filter((_, index) => index % 2 === 1)
  const minX = Math.min(minXFallback, ...xs)
  const maxX = Math.max(maxXFallback, ...xs)
  const minY = Math.min(minYFallback, ...ys)
  const maxY = Math.max(maxYFallback, ...ys)

  return {
    height: Math.max(maxY - minY, 1),
    maxX,
    maxY,
    minX,
    minY,
    width: Math.max(maxX - minX, 1),
  }
}

export const getLocalAnnotationVisualRect = ({
  annotation,
  baseSize,
}: {
  annotation: KonvaAnnotationTemplate
  baseSize: { width: number; height: number }
}): LocalAnnotationRect => {
  if (annotation.geometry.mode === 'points') {
    const extents = getPointExtents({
      maxXFallback: annotation.geometry.width ?? 1,
      maxYFallback: annotation.geometry.height ?? 1,
      points: annotation.geometry.points,
    })

    return {
      height: extents.height,
      width: extents.width,
      x: extents.minX,
      y: extents.minY,
    }
  }

  return {
    height: Math.max(baseSize.height, 1),
    width: Math.max(baseSize.width, 1),
    x: 0,
    y: 0,
  }
}

const getRotatedDocumentBounds = ({
  angle,
  height,
  left,
  top,
  width,
}: DocumentAnnotationBounds & { angle: number }): DocumentAnnotationBounds => {
  if (!angle) {
    return { height, left, top, width }
  }

  const radians = (angle * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const corners = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ].map((point) => ({
    x: left + point.x * cos - point.y * sin,
    y: top + point.x * sin + point.y * cos,
  }))
  const xs = corners.map((point) => point.x)
  const ys = corners.map((point) => point.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  return {
    height: Math.max(maxY - minY, 1),
    left: minX,
    top: minY,
    width: Math.max(maxX - minX, 1),
  }
}

export const getDocumentAnnotationBounds = ({
  annotation,
  subPageIds,
}: {
  annotation: KonvaAnnotationTemplate
  subPageIds: string[]
}): DocumentAnnotationBounds => {
  const y = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? subPageIds[0],
    relativeY: annotation.geometry.y,
    subPageIds,
  })

  if (annotation.geometry.mode === 'bounds') {
    return getRotatedDocumentBounds({
      angle: annotation.geometry.rotation ?? 0,
      height: Math.max(annotation.geometry.height ?? 1, 1),
      left: annotation.geometry.x,
      top: y,
      width: Math.max(annotation.geometry.width ?? 1, 1),
    })
  }

  const extents = getPointExtents({
    points: annotation.geometry.points,
  })
  const padding = Math.max((annotation.style.strokeWidth ?? 2) * 2, 12)

  return {
    height: Math.max(extents.height + padding * 2, 1),
    left: annotation.geometry.x + extents.minX - padding,
    top: y + extents.minY - padding,
    width: Math.max(extents.width + padding * 2, 1),
  }
}

export const getSelectionFrame = ({
  annotation,
  bubbleRadius = DEFAULT_BUBBLE_RADIUS,
  selectionPadding,
  strokeWidth,
  visualRect,
}: {
  annotation: KonvaAnnotationTemplate
  bubbleRadius?: number
  selectionPadding: number
  strokeWidth: number
  visualRect: LocalAnnotationRect
}): SelectionFrame => {
  const axisFrame: SelectionFrame = {
    mode: 'axis',
    center: {
      x: visualRect.x + visualRect.width / 2,
      y: visualRect.y + visualRect.height / 2,
    },
    cornerRadius: 6,
    rect: {
      height: visualRect.height + selectionPadding * 2,
      width: visualRect.width + selectionPadding * 2,
      x: visualRect.x - selectionPadding,
      y: visualRect.y - selectionPadding,
    },
  }

  if (!isLineBasedAnnotation(annotation)) {
    return axisFrame
  }

  const { angleDeg, length, ux, uy, x1, y1 } = getLineVector(
    annotation.geometry.points,
  )

  if (!length) {
    return axisFrame
  }

  const resolvedBubbleRadius = annotation.kind === 'bubble' ? bubbleRadius : 0
  const halfHeight = Math.max(
    strokeWidth / 2 + selectionPadding,
    resolvedBubbleRadius + selectionPadding,
    6,
  )
  const startPadding =
    annotation.kind === 'bubble'
      ? resolvedBubbleRadius + selectionPadding
      : ORIENTED_POINT_SELECTION_END_PADDING
  const endPadding = ORIENTED_POINT_SELECTION_END_PADDING
  const centerDistance = (length + endPadding - startPadding) / 2

  return {
    mode: 'oriented',
    angle: angleDeg,
    center: {
      x: x1 + ux * centerDistance,
      y: y1 + uy * centerDistance,
    },
    cornerRadius: Math.min(6, halfHeight),
    origin: { x: x1, y: y1 },
    rect: {
      height: halfHeight * 2,
      width: length + startPadding + endPadding,
      x: -startPadding,
      y: -halfHeight,
    },
  }
}

export const getArrowTextLayout = (
  annotation: KonvaAnnotationTemplate,
): ArrowTextLayout | null => {
  if (
    annotation.kind !== 'arrow' ||
    annotation.geometry.mode !== 'points' ||
    !annotation.text?.enabled ||
    (annotation.text.placement !== 'middle' &&
      annotation.text.placement !== 'bottom')
  ) {
    return null
  }

  const { dx, dy, length, x1, y1 } = getLineVector(annotation.geometry.points)

  if (length < 24) {
    return null
  }

  const placement = annotation.text.placement
  const textWidth = annotation.text.boxWidth ?? 100
  const fontSize = Math.max(
    Math.floor((annotation.text.fontSize ?? 14) - 1.5),
    10,
  )
  const text = annotation.text.maxLength
    ? (annotation.text.value ?? '').slice(0, annotation.text.maxLength)
    : (annotation.text.value ?? '')
  const textX =
    placement === 'middle'
      ? x1 + dx / 2 - textWidth / 2
      : x1 - textWidth / 2 - 10
  const textY =
    placement === 'middle' ? y1 + dy / 2 - fontSize / 2 : y1 - fontSize / 2

  return {
    arrowPoints: [x1, y1, x1 + dx, y1 + dy],
    fontSize,
    placement,
    text,
    textCornerRadius: annotation.text.boxCornerRadius ?? 0,
    textHeight: annotation.text.boxHeight ?? 20,
    textWidth,
    textX: Math.round(textX),
    textY: Math.round(textY),
  }
}

export const getBubbleLayout = (
  annotation: KonvaAnnotationTemplate,
): BubbleLayout | null => {
  if (annotation.kind !== 'bubble' || annotation.geometry.mode !== 'points') {
    return null
  }

  const { length, ux, uy, x1, x2, y1, y2 } = getLineVector(
    annotation.geometry.points,
  )

  if (length === 0) {
    return {
      bubbleCenter: { x: x1, y: y1 },
      bubbleRadius: DEFAULT_BUBBLE_RADIUS,
      linePoints: [x1, y1, x1, y1],
    }
  }

  return {
    bubbleCenter: { x: x1, y: y1 },
    bubbleRadius: DEFAULT_BUBBLE_RADIUS,
    linePoints: [
      x1 + ux * DEFAULT_BUBBLE_RADIUS,
      y1 + uy * DEFAULT_BUBBLE_RADIUS,
      x2,
      y2,
    ],
  }
}

export const getLineResizeHandleGeometry = (
  annotation: LineBasedAnnotation,
): LineResizeHandleGeometry[] => {
  const { length, ux, uy, x1, x2, y1, y2 } = getLineVector(
    annotation.geometry.points,
  )
  const offsetX = length ? ux * LINE_RESIZE_HANDLE_OFFSET : 0
  const offsetY = length ? uy * LINE_RESIZE_HANDLE_OFFSET : 0

  return [
    {
      handleOffsetX: -offsetX,
      handleOffsetY: -offsetY,
      index: 0,
      x: x1 - offsetX,
      y: y1 - offsetY,
    },
    {
      handleOffsetX: offsetX,
      handleOffsetY: offsetY,
      index: 1,
      x: x2 + offsetX,
      y: y2 + offsetY,
    },
  ]
}
